import { useQuery } from "@tanstack/react-query";
import { jobService } from "../services/database";
import Modal from "./Modal";
import type { Job } from "../types/database";

interface JobModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  organizationId: string;
}

export default function JobModal({
  isOpen,
  onClose,
  jobId,
  organizationId,
}: JobModalProps) {
  const {
    data: job,
    isLoading,
    error,
  } = useQuery<Job>({
    queryKey: ["job", jobId],
    queryFn: () => jobService.getJob(jobId, organizationId),
    enabled: isOpen && !!jobId && !!organizationId,
  });

  if (isLoading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Loading Job Details"
        size="lg"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </Modal>
    );
  }

  if (error || !job) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Error Loading Job"
        size="lg"
      >
        <div className="text-center py-8 text-red-600">
          Failed to load job details. Please try again.
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Job Details" size="lg">
      <div className="space-y-6">
        {/* Job Header */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {job.title}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Project #{job.project_number}
              </p>
            </div>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                job.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : job.status === "in_progress"
                  ? "bg-blue-100 text-blue-800"
                  : job.status === "cancelled"
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {job.status.replace("_", " ").toUpperCase()}
            </span>
          </div>
        </div>

        {/* Job Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Basic Information
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Client
                </label>
                <p className="text-sm text-gray-900">
                  {job.client || "Not specified"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Description
                </label>
                <p className="text-sm text-gray-900">
                  {job.description || "No description"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Location
                </label>
                <p className="text-sm text-gray-900">
                  {job.location
                    ? (() => {
                        try {
                          const locationDetails = JSON.parse(job.location);
                          return locationDetails.name || "Location specified";
                        } catch {
                          return job.location;
                        }
                      })()
                    : "Not specified"}
                </p>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Important Dates
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Prep Date
                </label>
                <p className="text-sm text-gray-900">
                  {job.prep_date
                    ? new Date(job.prep_date).toLocaleDateString("en-GB")
                    : "Not set"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Ship Date
                </label>
                <p className="text-sm text-gray-900">
                  {job.ship_date
                    ? new Date(job.ship_date).toLocaleDateString("en-GB")
                    : "Not set"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Start Date
                </label>
                <p className="text-sm text-gray-900">
                  {job.start_date
                    ? new Date(job.start_date).toLocaleDateString("en-GB")
                    : "Not set"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  End Date
                </label>
                <p className="text-sm text-gray-900">
                  {job.end_date
                    ? new Date(job.end_date).toLocaleDateString("en-GB")
                    : "Not set"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Return Date
                </label>
                <p className="text-sm text-gray-900">
                  {job.return_date
                    ? new Date(job.return_date).toLocaleDateString("en-GB")
                    : "Not set"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Assets */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Assets</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Checked Out Assets
              </h4>
              {job.assets && job.assets.length > 0 ? (
                <div className="text-sm text-gray-600">
                  {job.assets.length} asset(s) checked out
                </div>
              ) : (
                <p className="text-sm text-gray-500">No assets checked out</p>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        {job.notes && (
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-gray-900">Notes</h3>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-700">{job.notes}</p>
            </div>
          </div>
        )}

        {/* Created/Updated Info */}
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500">
            <div>
              <span className="font-medium">Created:</span>{" "}
              {new Date(job.created_at).toLocaleDateString("en-GB")} at{" "}
              {new Date(job.created_at).toLocaleTimeString("en-GB")}
            </div>
            <div>
              <span className="font-medium">Last Updated:</span>{" "}
              {new Date(job.updated_at).toLocaleDateString("en-GB")} at{" "}
              {new Date(job.updated_at).toLocaleTimeString("en-GB")}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
